
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import {
  defineComponent, onMounted, ref, computed,
} from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

interface VapidKey {
  id: string;
  label: string | null;
  vapid_public_key: string;
  vapid_private_key?: string;
  is_default: boolean;
  date_creation: string;
}

interface GenerateKeyRequest {
  label: string;
  vapid_private_key: string;
  vapid_public_key: string;
}

export default defineComponent({
  name: 'ShopVapidKeys',
  components: {
    SpmButton,
    Dialog,
    InputText,
    Tag,
    ProgressSpinner,
    ConfirmDialog,
    SpmOverlayPanel,
  },

  setup() {
    const { t } = useI18n();
    const confirm = useConfirm();

    const generatePanel = ref();
    const closeGeneratePanel = ref(false);
    const vapidKeys = ref<VapidKey[]>([]);
    const loading = ref(false);
    const settingDefault = ref<string | null>(null);
    const createdKey = ref<VapidKey | null>(null);

    const generateForm = ref<GenerateKeyRequest>({
      label: '',
      vapid_private_key: '',
      vapid_public_key: '',
    });

    // Validation rules for create form
    const rules = computed(() => ({
      label: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.labelRequired'),
          required,
        ),
      },
      vapid_private_key: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.privateKeyRequired'),
          required,
        ),
      },
      vapid_public_key: {
        required: helpers.withMessage(
          () => t('vapidKeys.errors.publicKeyRequired'),
          required,
        ),
      },
    }));

    const v$ = useVuelidate(rules, generateForm);

    const shopId = computed(() => UserState.activeShop?.id);

    const loadVapidKeys = async () => {
      if (!shopId.value) return;
      loading.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}`);
        if (response?.success) {
          vapidKeys.value = response.data;
        } else {
          throw new Error(response?.error?.message || t('vapidKeys.errors.loadFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const handleGenerateKey = async () => {
      if (!shopId.value) return;

      // Validate before submitting
      const isValid = await v$.value.$validate();
      if (!isValid) return;

      loading.value = true;
      try {
        const payload = {
          label: generateForm.value.label.trim() || null,
          vapid_private_key: generateForm.value.vapid_private_key.trim(),
          vapid_public_key: generateForm.value.vapid_public_key.trim(),
        };
        const response = await nestCall('post', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}`, {}, '', payload);
        if (response?.success) {
          closeGeneratePanel.value = true;
          createdKey.value = response.data;
          generateForm.value = {
            label: '',
            vapid_private_key: '',
            vapid_public_key: '',
          };
          v$.value.$reset();
          await loadVapidKeys();
          await showToastSuccess(t('vapidKeys.toasts.generated'));
        } else {
          const errorKey = response?.error || 'generateFailed';
          const errorMsg = t(`vapidKeys.errors.${errorKey}`) || t('vapidKeys.errors.generateFailed');
          throw new Error(errorMsg);
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        loading.value = false;
      }
    };

    const deleteKey = async (key: VapidKey) => {
      if (!shopId.value) return;
      try {
        const response = await nestCall('delete', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}/${key.id}`);
        if (response?.success) {
          vapidKeys.value = vapidKeys.value.filter((k) => k.id !== key.id);
          await showToastSuccess(t('vapidKeys.toasts.deleted'));
        } else {
          const errorKey = response?.error || 'deleteFailed';
          const errorMsg = t(`vapidKeys.errors.${errorKey}`) || t('vapidKeys.errors.deleteFailed');
          await showToastError(errorMsg);
        }
      } catch (err: any) {
        await showToastError(err.message);
      }
    };

    const setDefaultKey = async (key: VapidKey) => {
      if (!shopId.value) return;
      settingDefault.value = key.id;
      try {
        const response = await nestCall('put', NestApiType.DEFAULT, `/shop/vapid-keys/${shopId.value}/${key.id}/default`);
        if (response?.success) {
          await loadVapidKeys();
          await showToastSuccess(t('vapidKeys.toasts.defaultSet'));
        } else {
          throw new Error(response?.error || t('vapidKeys.errors.setDefaultFailed'));
        }
      } catch (err: any) {
        await showToastError(err.message);
      } finally {
        settingDefault.value = null;
      }
    };

    const copyPublicKey = async (key: VapidKey) => {
      try {
        await navigator.clipboard.writeText(key.vapid_public_key);
        await showToastSuccess(t('vapidKeys.toasts.publicKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyPrivateKey = async (key: VapidKey) => {
      if (!key.vapid_private_key) return;
      try {
        await navigator.clipboard.writeText(key.vapid_private_key);
        await showToastSuccess(t('vapidKeys.toasts.privateKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyCreatedPublicKey = async () => {
      if (!createdKey.value) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.vapid_public_key);
        await showToastSuccess(t('vapidKeys.toasts.publicKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const copyCreatedPrivateKey = async () => {
      if (!createdKey.value?.vapid_private_key) return;
      try {
        await navigator.clipboard.writeText(createdKey.value.vapid_private_key);
        await showToastSuccess(t('vapidKeys.toasts.privateKeyCopied'));
      } catch (err) {
        await showToastError('Failed to copy');
      }
    };

    const toggleGeneratePanel = (event: Event) => {
      generatePanel.value.toggle(event);
      closeGeneratePanel.value = false;
    };

    const confirmDelete = (key: VapidKey) => {
      confirm.require({
        message: t('vapidKeys.confirmDelete', { label: key.label || t('vapidKeys.unnamedKey') }),
        header: t('confirmation'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          deleteKey(key);
        },
      });
    };

    const confirmSetDefault = (key: VapidKey) => {
      confirm.require({
        message: t('vapidKeys.confirmSetDefault', { label: key.label || t('vapidKeys.unnamedKey') }),
        header: t('confirmation'),
        icon: 'fas fa-star',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          setDefaultKey(key);
        },
      });
    };

    onMounted(loadVapidKeys);

    return {
      t,
      v$,
      vapidKeys,
      loading,
      settingDefault,
      createdKey,
      generateForm,
      generatePanel,
      closeGeneratePanel,
      loadVapidKeys,
      handleGenerateKey,
      confirmDelete,
      confirmSetDefault,
      copyPublicKey,
      copyPrivateKey,
      copyCreatedPublicKey,
      copyCreatedPrivateKey,
      toggleGeneratePanel,
    };
  },
});
